import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { PhotoRecapRow } from '../../../../api/batch';
import { exportPhotos, photoRecap } from '../../../../api/batch';
import { useIseeEnabled, useIseeSessions } from '../../../../api/isee';
import type { IseeQueryState } from '../../../../components/cards/cards-url-state';
import {
  cleanQuery,
  toSessionQuery,
} from '../../../../components/cards/isee-filters';
import { IseeRecipients } from '../../../../components/cards/IseeRecipients';
import { ErrorMessage } from '../../../../components/common/ErrorMessage';
import { downloadBlob } from '../../../../utils/downloadBlob';

export const Route = createFileRoute('/_auth/photos/export/')({
  component: PhotoExportPage,
});

/**
 * Admin only: pick an ISEE cohort as in the card generator, check who has a
 * photo, then download those photos as a ZIP.
 */
function PhotoExportPage() {
  const { t } = useTranslation();
  const iseeEnabled = useIseeEnabled();
  const iseeSessions = useIseeSessions(iseeEnabled);
  const [iseeQuery, setIseeQuery] = useState<IseeQueryState>({});
  const query = toSessionQuery(cleanQuery(iseeSessions.tree, iseeQuery));
  const recap = useMutation({ mutationFn: photoRecap });
  const zip = useMutation({
    mutationFn: exportPhotos,
    onSuccess: (blob, q) => downloadBlob(blob, `photos-${q.cohort}.zip`),
  });
  const withPhoto = recap.data?.filter((r) => r.hasPhoto).length ?? 0;
  const error = recap.error ?? zip.error;

  return (
    <section className="epi-card tone-blue">
      <div className="epi-card-head">
        <span className="num">E</span>
        <span className="title">{t('photoExport.title')}</span>
        <span className="meta">ISEE · ZIP · UPN</span>
      </div>
      <p className="my-4 text-sm text-muted">{t('photoExport.description')}</p>
      <IseeRecipients
        iseeEnabled={iseeEnabled}
        iseeSessions={iseeSessions}
        query={iseeQuery}
        onQueryChange={(q) => {
          // A recap only stands for the filters it was checked with.
          recap.reset();
          zip.reset();
          setIseeQuery(q);
        }}
      />
      <button
        type="button"
        onClick={() => query && recap.mutate(query)}
        disabled={!query || recap.isPending}
        className="btn-ghost mt-4 w-full justify-center disabled:cursor-not-allowed disabled:opacity-40"
      >
        {recap.isPending ? t('photoExport.checking') : t('photoExport.check')}
      </button>
      {recap.data && <PhotoRecap rows={recap.data} withPhoto={withPhoto} />}
      {recap.data && (
        <button
          type="button"
          onClick={() => query && zip.mutate(query)}
          disabled={withPhoto === 0 || zip.isPending}
          className="btn-primary mt-4 w-full justify-center disabled:cursor-not-allowed disabled:opacity-40"
        >
          {zip.isPending
            ? t('photoExport.exporting')
            : t('photoExport.submit', { n: withPhoto })}
        </button>
      )}
      {error && (
        <div className="mt-4">
          <ErrorMessage message={error.message} />
        </div>
      )}
    </section>
  );
}

/** Each student of the cohort, with or without a photo — missing ones first. */
function PhotoRecap({
  rows,
  withPhoto,
}: {
  rows: PhotoRecapRow[];
  withPhoto: number;
}) {
  const { t } = useTranslation();
  if (rows.length === 0) {
    return (
      <p className="mt-4 text-sm text-muted">{t('cardGenerator.isee.none')}</p>
    );
  }
  return (
    <div className="mt-4 max-h-96 overflow-auto border border-rule">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-card">
          <tr>
            <th
              colSpan={3}
              className="px-2 pt-2 pb-1 text-left text-sm font-medium text-ink"
            >
              {t('photoExport.recap', {
                withPhoto,
                without: rows.length - withPhoto,
              })}
            </th>
          </tr>
          <tr className="border-b border-rule text-left text-muted">
            <th className="px-2 py-1 font-medium">{t('photoExport.name')}</th>
            <th className="px-2 py-1 font-medium">UPN</th>
            <th className="px-2 py-1 font-medium">{t('photoExport.photo')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.login}>
              <td className="px-2 py-1 text-ink">{r.name || '—'}</td>
              <td className="px-2 py-1 text-muted">{r.login}</td>
              <td
                className={`px-2 py-1 whitespace-nowrap ${r.hasPhoto ? 'text-blue' : 'text-magenta'}`}
              >
                {r.hasPhoto
                  ? `✓ ${t('photoExport.hasPhoto')}`
                  : `✗ ${t('photoExport.noPhoto')}`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
